import { ComposerTrayItem, ComposerTrayMenu, ComposerTrayPopover, ComposerTraySection } from "#features/conversations/components/conversation-input/ComposerTray.tsx";
import { ASSISTANT_MODE_BY_EFFORT, MIXPANEL_ASSISTANT_MODE, useAssistantModeStore, type ReasoningEffort } from "#features/conversations/stores/useAssistantModeStore.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Text } from "@hopper-ui/components";
import { IconAtom, IconBolt, IconCheck, IconSparklesTwo } from "@intentui/icons";
import { useCallback, type FC, type RefObject, type SVGProps } from "react";
import { MenuTrigger, type Selection } from "react-aria-components";
import "./ReasoningEffortMenu.css";

const EFFORTS = [
    { id: "auto", labelKey: "reasoningEffortAuto", Icon: IconSparklesTwo },
    { id: "standard", labelKey: "reasoningEffortStandard", Icon: IconBolt },
    { id: "high", labelKey: "reasoningEffortHigh", Icon: IconAtom }
] as const satisfies ReadonlyArray<{ id: ReasoningEffort; labelKey: string; Icon: FC<SVGProps<SVGSVGElement>> }>;

const LABEL_KEYS = Object.fromEntries(EFFORTS.map(({ id, labelKey }) => [id, labelKey])) as Record<ReasoningEffort, string>;
const TRAY_OFFSET = 4;
const TRAY_INLINE_SIZE = 220;

interface Props {
    isDisabled: boolean;
    // The bar the picker opens above, right edge to right edge.
    trayAnchorRef: RefObject<HTMLElement | null>;
}

// A muted "Auto" label beside the send slot opens a small tray, on the "+" tray's surface and rows.
export function ReasoningEffortMenu({ isDisabled, trayAnchorRef }: Props) {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const reasoningEffort = useAssistantModeStore(state => state.reasoningEffort);
    const setReasoningEffort = useAssistantModeStore(state => state.setReasoningEffort);
    const label = tShared(LABEL_KEYS[reasoningEffort]);

    const handleSelectionChange = useCallback((keys: Selection) => {
        const [key] = keys === "all" ? [] : [...keys];
        if (key === undefined) {
            return;
        }
        const next = key as ReasoningEffort;
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Conversation Input",
            Trigger: "Toggle Assistant Mode",
            "Assistant Mode": MIXPANEL_ASSISTANT_MODE[ASSISTANT_MODE_BY_EFFORT[next]]
        });
        setReasoningEffort(next);
    }, [setReasoningEffort, trackEvent]);

    return (
        <MenuTrigger>
            <Button
                variant="ghost-secondary"
                size="sm"
                borderRadius="circle"
                aria-label={`${tShared("reasoningEffort")}: ${label}`}
                isDisabled={isDisabled}
                className="wai-composer-ghost wai-reasoning-effort-trigger"
            >
                <Text size="xs" color="neutral-weak">{label}</Text>
            </Button>
            <ComposerTrayPopover
                anchorRef={trayAnchorRef}
                placement="top end"
                offset={TRAY_OFFSET}
                inlineSize={TRAY_INLINE_SIZE}
                className="wai-reasoning-effort-tray"
            >
                <ComposerTrayMenu
                    aria-label={tShared("reasoningEffort")}
                    selectionMode="single"
                    disallowEmptySelection
                    selectedKeys={[reasoningEffort]}
                    onSelectionChange={handleSelectionChange}
                >
                    <ComposerTraySection title={tShared("reasoningEffort")}>
                        {EFFORTS.map(({ id, labelKey, Icon }) => (
                            <ComposerTrayItem
                                key={id}
                                id={id}
                                name={tShared(labelKey)}
                                icon={<Icon />}
                                status={id === reasoningEffort && <IconCheck />}
                            />
                        ))}
                    </ComposerTraySection>
                </ComposerTrayMenu>
            </ComposerTrayPopover>
        </MenuTrigger>
    );
}
